﻿import { AlertCircle, Loader2, Receipt, Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import type { OrderStatus } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useOrdersInfiniteQuery } from "@/features/orders/api";
import { cn } from "@/lib/utils";

const STATUS_LABELS: Record<OrderStatus, string> = {
  Pending: "Ödeme Bekliyor",
  Paid: "Ödendi",
  Cancelled: "İptal",
  Expired: "Süresi Doldu",
  Refunded: "İade Edildi",
};

const STATUS_VARIANTS: Record<
  OrderStatus,
  "success" | "warning" | "secondary" | "destructive" | "outline" | "default"
> = {
  Pending: "warning",
  Paid: "success",
  Cancelled: "destructive",
  Expired: "secondary",
  Refunded: "outline",
};

const STATUS_FILTERS: Array<{ value: OrderStatus | ""; label: string }> = [
  { value: "", label: "Tümü" },
  { value: "Paid", label: "Ödendi" },
  { value: "Pending", label: "Bekliyor" },
  { value: "Refunded", label: "İade" },
  { value: "Cancelled", label: "İptal" },
  { value: "Expired", label: "Süresi Doldu" },
];

export function OrdersPage() {
  return (
    <DashboardPage>
      <OrdersView />
    </DashboardPage>
  );
}

/** Basit debounce — search input'unun her tuşta API'yi dövmesini önler. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function OrdersView() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<OrderStatus | "">("");
  const debouncedSearch = useDebouncedValue(search, 350);

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      status: status || undefined,
    }),
    [debouncedSearch, status],
  );

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useOrdersInfiniteQuery(filters);

  const items = useMemo(
    () => data?.pages.flatMap((p) => p?.items ?? []) ?? [],
    [data],
  );
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  const hasFilters = !!debouncedSearch || !!status;
  const isEmpty = !isLoading && items.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Siparişler</h1>
        <p className="text-sm text-muted-foreground">
          Mağazalarındaki tüm biletleri ve durumlarını buradan görebilirsin.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sipariş no, etkinlik, isim, e-posta veya telefon"
            className="pl-9 pr-9"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
              aria-label="Aramayı temizle"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex overflow-x-auto gap-1 rounded-md border bg-card p-1 sm:w-auto">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatus(f.value)}
              className={cn(
                "whitespace-nowrap rounded px-3 py-1.5 text-xs font-medium transition",
                status === f.value
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-muted",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          Siparişler yüklenemedi. Lütfen sayfayı yenile.
        </div>
      ) : isEmpty ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
            <Receipt className="h-8 w-8" />
            <div>
              <p className="font-medium text-foreground">
                {hasFilters ? "Sonuç yok" : "Henüz sipariş yok"}
              </p>
              <p className="mt-1 text-sm">
                {hasFilters
                  ? "Filtreleri değiştirip tekrar dene."
                  : "Etkinliğini yayına al ve mağazana ekle; ilk sipariş geldiğinde burada görürsün."}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="text-xs text-muted-foreground">
            {items.length} / {totalCount} sipariş gösteriliyor
          </div>

          {/* Desktop tablo (sm+) */}
          <Card className="hidden sm:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Sipariş</th>
                      <th className="px-4 py-2 text-left font-medium">Etkinlik</th>
                      <th className="px-4 py-2 text-left font-medium">Alıcı</th>
                      <th className="px-4 py-2 text-right font-medium">Bilet</th>
                      <th className="px-4 py-2 text-right font-medium">Tutar</th>
                      <th className="px-4 py-2 text-left font-medium">Durum</th>
                      <th className="px-4 py-2 text-left font-medium">Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((o) => (
                      <tr
                        key={o.orderNumber}
                        className="border-t hover:bg-muted/30"
                      >
                        <td className="px-4 py-2 font-mono text-xs">
                          <Link
                            to={`/orders/${o.orderNumber}`}
                            className="hover:underline"
                          >
                            {o.orderNumber}
                          </Link>
                        </td>
                        <td className="px-4 py-2">
                          <div className="font-medium">{o.eventTitle}</div>
                          <div className="text-xs text-muted-foreground">
                            {o.shopSlug}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div>
                            {o.buyerFirstName} {o.buyerLastName}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {o.buyerEmail}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {o.ticketCount}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">
                          {formatPrice(o.total, o.currency)}
                        </td>
                        <td className="px-4 py-2">
                          <Badge variant={STATUS_VARIANTS[o.status]}>
                            {STATUS_LABELS[o.status]}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {formatDate(o.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Mobile card listesi (<sm) */}
          <div className="space-y-2 sm:hidden">
            {items.map((o) => (
              <Link
                key={o.orderNumber}
                to={`/orders/${o.orderNumber}`}
                className="block rounded-md border bg-card p-3 transition hover:bg-muted/30"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {o.eventTitle}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {o.buyerFirstName} {o.buyerLastName} · {o.buyerEmail}
                    </div>
                  </div>
                  <Badge variant={STATUS_VARIANTS[o.status]}>
                    {STATUS_LABELS[o.status]}
                  </Badge>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-mono">{o.orderNumber}</span>
                  <span className="tabular-nums font-medium text-foreground">
                    {formatPrice(o.total, o.currency)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{o.ticketCount} bilet</span>
                  <span>{formatDate(o.createdAt)}</span>
                </div>
              </Link>
            ))}
          </div>

          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Yükleniyor
                  </>
                ) : (
                  "Daha fazla yükle"
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const PRICE_FMT = new Intl.NumberFormat("tr-TR", {
  style: "decimal",
  maximumFractionDigits: 2,
});

function formatPrice(value: number, currency: string) {
  return `${PRICE_FMT.format(value)} ${currency}`;
}

const DATE_FMT = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Istanbul",
});

function formatDate(iso: string) {
  return DATE_FMT.format(new Date(iso));
}
